// Blueprint review. Dual mode:
//  - new agent: blueprint passed via location.state, save creates the row.
//  - existing agent: id in URL, fetches the saved blueprint from server.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Settings } from 'lucide-react';
import type { AgentBlueprint, AgentDetail, IntegrationHealthStatus } from '@qualibot/shared';
import { Card } from '@/components/primitives/Card';
import { Badge, type BadgeTone } from '@/components/primitives/Badge';
import { StatusBadge } from '@/components/primitives/StatusBadge';
import { Btn } from '@/components/primitives/Btn';
import { PageContainer, PageHeader, SectionLabel } from '@/components/PageHeader';
import { WizardSteps } from '@/components/WizardSteps';
import { OrgChart } from '@/components/OrgChart';
import { createAgent, fetchAgent } from '@/lib/agents-api';
import { extractApiError } from '@/lib/api-client';

const RISK_TONE: Record<string, BadgeTone> = {
  LOW: 'ok',
  MEDIUM: 'info',
  HIGH: 'warn',
  CRITICAL: 'err',
};

interface NewBlueprintState {
  blueprint?: AgentBlueprint;
}

export function AgentBlueprintPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Mode A: new-agent blueprint from location.state
  const stateBlueprint = (location.state as NewBlueprintState | null)?.blueprint ?? null;

  // Mode B: existing agent
  const isNewMode = !id || id === 'new';
  const existing = useQuery({
    queryKey: ['agent', id],
    queryFn: () => fetchAgent(id as string),
    enabled: !isNewMode,
  });

  const blueprint: AgentBlueprint | null = isNewMode
    ? stateBlueprint
    : (existing.data?.blueprintJson ?? null);
  const detail: AgentDetail | null = isNewMode ? null : (existing.data ?? null);

  const [savedId, setSavedId] = useState<string | null>(null);
  const saveMutation = useMutation({
    mutationFn: (bp: AgentBlueprint) => createAgent({ blueprint: bp }),
    onSuccess: (agent) => {
      setSavedId(agent.id);
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });

  useEffect(() => {
    if (savedId) {
      const t = setTimeout(() => navigate(`/agents/${savedId}`, { replace: true }), 600);
      return () => clearTimeout(t);
    }
  }, [savedId, navigate]);

  // Guards
  if (isNewMode && !stateBlueprint) {
    return (
      <PageContainer>
        <PageHeader
          title="No blueprint to review"
          description="Start by describing the agent and we'll generate a blueprint."
          actions={<Btn kind="primary" onClick={() => navigate('/agents/new')}>Start over</Btn>}
        />
      </PageContainer>
    );
  }
  if (!isNewMode && existing.isLoading) {
    return (
      <PageContainer>
        <div className="text-[13px] text-text-3 py-10 text-center">Loading agent…</div>
      </PageContainer>
    );
  }
  if (!blueprint) {
    return (
      <PageContainer>
        <PageHeader
          title={detail?.name ?? 'Agent'}
          description="This agent has no blueprint stored yet."
        />
      </PageContainer>
    );
  }

  const requiredMcps: { name: string; status: IntegrationHealthStatus }[] =
    detail?.requiredMcps?.length
      ? detail.requiredMcps.map((m) => ({ name: m.name, status: m.status }))
      : blueprint.requiredMcps.map((n) => ({ name: n, status: 'Connected' }));

  const saveError = saveMutation.isError ? extractApiError(saveMutation.error) : null;

  return (
    <PageContainer>
      <div className="max-w-[920px]">
        <PageHeader
          title={`Agent blueprint — ${blueprint.name}`}
          description={
            isNewMode
              ? 'Review the generated configuration before connecting tools'
              : `Tone: ${blueprint.tone}`
          }
          actions={
            isNewMode ? (
              <>
                <Btn onClick={() => navigate('/agents/new')}>Back</Btn>
                <Btn
                  kind="primary"
                  onClick={() => saveMutation.mutate(blueprint)}
                  disabled={saveMutation.isPending || !!savedId}
                >
                  {savedId
                    ? 'Saved →'
                    : saveMutation.isPending
                      ? 'Saving…'
                      : 'Save agent draft'}
                </Btn>
              </>
            ) : (
              <Btn onClick={() => detail && navigate(`/agents/${detail.id}/skills`)}>
                Edit skills
              </Btn>
            )
          }
        />
        <WizardSteps current={isNewMode ? 1 : 4} />

        {saveError && (
          <div
            role="alert"
            className="mb-3.5 px-3 py-2 rounded-md text-[12.5px]"
            style={{ background: 'var(--err-soft)', color: 'var(--err)' }}
          >
            {saveError}
          </div>
        )}

        <Card>
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-[13.5px]">Purpose</span>
          </div>
          <div className="text-[14px] text-text-2 leading-relaxed">{blueprint.description}</div>
        </Card>

        <div className="grid grid-cols-3 gap-3.5 mt-3.5">
          <SmallCard label="Languages" value={blueprint.languages.join(' · ')} />
          <SmallCard label="Tone" value={blueprint.tone} />
          <SmallCard label="Primary channel" value={blueprint.primaryChannel} />
        </div>

        <SectionLabel>Skills</SectionLabel>
        <div className="grid grid-cols-3 gap-3">
          {blueprint.skills.map((s) => (
            <Card key={s.name} pad={14}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-[13.5px]">{s.name}</span>
                <Badge tone={RISK_TONE[s.risk] ?? 'default'}>
                  {s.risk.charAt(0) + s.risk.slice(1).toLowerCase()} risk
                </Badge>
              </div>
            </Card>
          ))}
        </div>

        <SectionLabel>Required MCP tools</SectionLabel>
        <div className="grid grid-cols-4 gap-3">
          {requiredMcps.map((m) => (
            <Card key={m.name} pad={14}>
              <div className="font-medium text-[13.5px] mb-2">{m.name}</div>
              <StatusBadge status={m.status} />
            </Card>
          ))}
        </div>

        <SectionLabel>Guardrails</SectionLabel>
        <Card>
          <ul className="m-0 pl-5 text-[13.5px] text-text-2 flex flex-col gap-1.5">
            {blueprint.guardrails.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </Card>

        <SectionLabel>Internal structure</SectionLabel>
        <Card>
          <OrgChart />
          {detail && (
            <div className="flex items-center gap-2 mt-4">
              <Btn size="sm" icon={Settings} onClick={() => navigate(`/agents/${detail.id}/skills`)}>
                Edit skills
              </Btn>
            </div>
          )}
        </Card>

        {!isNewMode && detail && (
          <div className="flex items-center justify-between mt-5">
            <Btn icon={ArrowLeft} onClick={() => navigate('/agents')}>
              Back to agents
            </Btn>
            <Btn kind="primary" icon={ArrowRight} onClick={() => navigate(`/agents/${detail.id}/skills`)}>
              Configure skills
            </Btn>
          </div>
        )}
      </div>
    </PageContainer>
  );
}

function SmallCard({ label, value }: { label: string; value: string }) {
  return (
    <Card pad={16}>
      <div className="text-[12px] text-text-3 mb-1.5">{label}</div>
      <div className="font-medium text-[14px]">{value}</div>
    </Card>
  );
}
